import { CustomTranslationFnc } from '@hooks/useCustomTranslation';
import { StarEmptyIcon, StarHalfEmptyIcon, StarIcon } from '@gemini/fe-ds-core';

// utils.ts
export const DAY_ORDER = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
] as const;
export type DayCode = (typeof DAY_ORDER)[number];

export type Hour = {
  day: string[];
  openingTime: string; // "HH:mm"
  closingTime: string; // "HH:mm"
};

export function formatTime12Hour(time: string): string {
  const match = /^(\d{1,2}):([0-5]\d)$/.exec(time);
  if (!match) return time;

  const hours = Number(match[1]);
  if (hours > 23) return time;

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = String(hours % 12 || 12).padStart(2, '0');

  return `${displayHours}:${match[2]} ${period}`;
}

export function formatHours12Hour(hours: Hour[]): Hour[] {
  return hours.map(hour => ({
    ...hour,
    openingTime: formatTime12Hour(hour.openingTime),
    closingTime: formatTime12Hour(hour.closingTime),
  }));
}

/** Costruisce, per ogni giorno, la stringa delle fasce (es: "09:00 - 13:00, 14:00 - 18:00") o "Close". */
export function buildDayRanges(hours: Hour[], t: CustomTranslationFnc) {
  const byDay: Record<string, string[]> = {
    MON: [],
    TUE: [],
    WED: [],
    THU: [],
    FRI: [],
    SAT: [],
    SUN: [],
  };

  (hours ?? []).forEach(h => {
    const range = `${h.openingTime} - ${h.closingTime}`;
    h.day.forEach(d => {
      if (!byDay[d].includes(range)) byDay[d].push(range); // dedup
    });
  });

  const result: Record<DayCode, string> = {} as any;
  DAY_ORDER.forEach(d => {
    result[d] = byDay[d].length
      ? byDay[d].join(', ')
      : t('practiceCard.closed');
  });
  return result;
}

/**Funzione per ottenere l'orario corrente */
export function isOpenNow(hours: Hour[], date: Date = new Date()): boolean {
  // Ricava giorno corrente (MON, TUE, ecc.)
  const dayCode = DAY_ORDER[date.getDay() === 0 ? 6 : date.getDay() - 1]; // getDay(): 0=DOM, 6=SAT
  const nowMinutes = date.getHours() * 60 + date.getMinutes();

  // Filtra gli orari validi per quel giorno
  const todaysHours = hours.filter(h => h.day.includes(dayCode));

  // Se non ci sono orari, è chiuso
  if (todaysHours.length === 0) return false;

  // Controlla se l'ora corrente rientra in uno degli intervalli
  return todaysHours.some(h => {
    const [openH, openM] = h.openingTime.split(':').map(Number);
    const [closeH, closeM] = h.closingTime.split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;

    // Gestione orari oltre la mezzanotte (es: 22:00 - 02:00)
    if (closeMinutes < openMinutes) {
      return nowMinutes >= openMinutes || nowMinutes < closeMinutes;
    }

    return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
  });
}

/**Funzione per ottenere il closing time corrente */
export function getCurrentClosingTime(
  hours: Hour[],
  date: Date = new Date(),
): string | null {
  const dayCode = DAY_ORDER[date.getDay() === 0 ? 6 : date.getDay() - 1];
  const nowMinutes = date.getHours() * 60 + date.getMinutes();
  const todaysHours = hours.filter(h => h.day.includes(dayCode));
  if (todaysHours.length === 0) return null;

  for (const h of todaysHours) {
    const [openH, openM] = h.openingTime.split(':').map(Number);
    const [closeH, closeM] = h.closingTime.split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;

    // Gestione overnight
    if (closeMinutes < openMinutes) {
      if (nowMinutes >= openMinutes || nowMinutes < closeMinutes) {
        return h.closingTime;
      }
    } else {
      if (nowMinutes >= openMinutes && nowMinutes < closeMinutes) {
        return h.closingTime;
      }
    }
  }

  return null; // non siamo in nessuna fascia attiva
}

export function openGoogleReviews(placeId: string) {
  const url = `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Return a react node with the right number of stars (full, empty, half empty)
 * @param rating - Numero da 0 a 5, può includere .5
 * @returns ReactNode with stars icons
 */
export const renderStars = (rating?: number, color?: string) => {
  if (!rating) return null;

  const stars = [];
  const normalized = Math.max(0, Math.min(5, Number(rating) || 0));
  const fullStars = Math.floor(normalized);
  const hasHalf = normalized - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  for (let i = 0; i < fullStars; i++) {
    stars.push(<StarIcon key={`star-full-${i}`} color={color} />);
  }

  if (hasHalf) {
    stars.push(<StarHalfEmptyIcon key="star-half" color={color} />);
  }

  for (let i = 0; i < emptyStars; i++) {
    stars.push(<StarEmptyIcon key={`star-empty-${i}`} color={color} />);
  }

  return <>{stars}</>;
};

export function getTodayOpeningHours(
  hours: Hour[],
  date: Date = new Date(),
): string[] {
  if (!hours || hours.length === 0) return [];

  const dayCode = DAY_ORDER[date.getDay() === 0 ? 6 : date.getDay() - 1];

  const todaysHours = hours
    .filter(h => h.day.includes(dayCode))
    .map(h => `${h.openingTime} - ${h.closingTime}`);

  return todaysHours;
}
